import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from './reduxHook';
import useNoChangeWarning from './useNoChangeWarning';
import { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import getClosestMileage from '../utils/getClosestMileage';
import { ITopUpMilesForm } from '../pages/mta/TopUpMiles';
import { checkFormConditions } from '@acorn-portal/shared/src/pages/mta/utils';
import { ROUTE_PATHS } from '../utils/Routes';
import { handleTopUpMilesRating } from '../models/midTermAdjustment-model';
import { IMidTermAdjustmentState, updateMidTermAdjustmentDetails } from '../store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '../store/midTermAdjustmentRatingResponse.slice';
import { ListNames, Option } from '../store/referenceData.slice';
import { isTelematics } from '../utils';

// Extract form logic to a separate function
const useMileageFormLogic = (
	midTermAdjustment: IMidTermAdjustmentState,
	lists: Record<ListNames, Option[]>,
	setLoading: (loading: boolean) => void,
) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [hasChanged] = useNoChangeWarning();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const mtaChanges = useAppSelector((redux) => redux.midTermAdjustment);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);

	// Generate mileage options with proper labels
	const mileageOptions = (lists.millageIncrementOptions as IOptions[]).map((option) => {
		if (option.value) {
			return {
				...option,
				label: option.value.toString(),
			};
		}
		return option;
	});

	// Calculate initial values
	const closestMileage = getClosestMileage(
		lists.millageIncrementOptions as IOptions[],
		midTermAdjustment?.vehicleDetails?.annualMileage,
	);
	const parsedClosestMileage = parseInt(closestMileage, 10);
	const getInitialValues = () => ({
		annualMileage: Number.isNaN(parsedClosestMileage) ? 0 : parsedClosestMileage,
	});

	// Handle form submission
	const onSubmit = async (values: ITopUpMilesForm): Promise<void> => {
		const { annualMileage } = values;
		const activePolicyValues = {
			annualMileage: getClosestMileage(
				lists.millageIncrementOptions as IOptions[],
				activePolicyDetails.vehicleDetails.annualMileage,
			),
		};

		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, values),
			hasChanged(getInitialValues(), values),
			mtaResponse,
			mtaChanges.changeOfUsage,
		);

		// Early returns for various conditions
		if (!continueToRate && !navigateToSummary) {
			return;
		}
		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.topUpMilesPrice);
			return;
		}

		setLoading(true);
		try {
			const result = await handleTopUpMilesRating({
				midTermAdjustment: mtaChanges,
				annualMileage,
				product: fullPolicy.product,
			});

			dispatch(
				updateMidTermAdjustmentDetails({
					...mtaChanges,
					changeOfUsage: updateSection,
					vehicleDetails: {
						...mtaChanges.vehicleDetails,
						annualMileage,
					},
				}),
			);

			if (!result) {
				throw new Error('Rating response is undefined');
			}

			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}

			dispatch(setMidTermAdjustmentRatingResponse(result));
			setLoading(false);
			navigate(ROUTE_PATHS.topUpMilesPrice);
		} catch (error) {
			console.error('Error during form submission:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return {
		initialValues: getInitialValues(),
		mileageOptions,
		onSubmit,
		isTelematics: isTelematics(fullPolicy.product),
		fullPolicy,
	};
};

export default useMileageFormLogic;
